import { useEffect, useRef } from 'react'

/**
 * SCRUM-1032 — the browser's own way out asks first while work is not saved.
 *
 * Back, Next and Exit flush the step; a reload, a closed tab or the browser's
 * Back button flush nothing, and used to leave without a word (wizard review).
 * `shouldWarn` is read at the moment the page is being left, so it can read
 * refs and costs nothing in between.
 */
export const useWarnBeforeUnload = (shouldWarn: () => boolean) => {
  const shouldWarnRef = useRef(shouldWarn)
  shouldWarnRef.current = shouldWarn

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarnRef.current()) return
      event.preventDefault()
      // Older browsers only prompt on a set return value; the text is ignored.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])
}

/** How long a failed gate count waits before it is asked again. */
export const COUNT_RETRY_MS = 5_000

/**
 * SCRUM-1032 — a gate count whose request failed is asked again.
 *
 * Queries run with `retry: 0`, so a failed count stayed unknown for the rest of
 * the step, and an unknown count holds Next ("checking"). Re-asked on a timer
 * for as long as it keeps failing — `errorUpdatedAt` moves on each failure.
 */
export const useRetryFailedQuery = (
  query: { isError: boolean; errorUpdatedAt: number; refetch: () => unknown },
  retryMs: number = COUNT_RETRY_MS,
) => {
  const refetchRef = useRef(query.refetch)
  refetchRef.current = query.refetch

  useEffect(() => {
    if (!query.isError) return
    const timer = window.setTimeout(() => void refetchRef.current(), retryMs)
    return () => window.clearTimeout(timer)
  }, [query.isError, query.errorUpdatedAt, retryMs])
}
